import React from "react";
import {
  createStyles,
  Box,
  Stack,
  Text,
  Center,
  Image,
  Space,
  Group,
  Anchor,
} from "@mantine/core";
import {
  IconDiamond,
  IconHexagon,
  IconFlame,
  IconSparkles,
  IconAtom,
  IconStar,
} from "@tabler/icons-react";
import { Link, useNavigate } from "react-router-dom";

function FloatingIcon({ icon: Icon, size, color, top, left, right, rotate, opacity }) {
  return (
    <Box
      sx={{
        position: "absolute",
        top,
        left,
        right,
        transform: rotate ? `rotate(${rotate}deg)` : undefined,
        opacity: opacity || 0.15,
        pointerEvents: "none",
        zIndex: 0,
        "@media (max-width: 768px)": {
          display: "none",
        },
      }}
    >
      <Icon size={size || 40} color={color} stroke={1.5} />
    </Box>
  );
}

function HomePage({ children, ...otherProps }) {
  const { classes } = useStyles();
  const navigation = useNavigate();
  return (
    <Box className={classes.pageWrapper} {...otherProps}>
      {/* Decorative Icons — left side */}
      <FloatingIcon icon={IconDiamond} size={60} color="#5622F4" top="4%" left="4%" rotate={-15} opacity={0.15} />
      <FloatingIcon icon={IconSparkles} size={38} color="#7B4DFF" top="15%" left="2%" rotate={20} opacity={0.18} />
      <FloatingIcon icon={IconHexagon} size={48} color="#9B6BFF" top="30%" left="3%" rotate={12} opacity={0.13} />
      <FloatingIcon icon={IconStar} size={34} color="#5622F4" top="45%" left="5%" rotate={5} opacity={0.16} />
      <FloatingIcon icon={IconAtom} size={44} color="#4A1DC9" top="60%" left="2%" rotate={-25} opacity={0.14} />
      <FloatingIcon icon={IconSparkles} size={42} color="#7B4DFF" top="75%" left="4%" rotate={30} opacity={0.15} />
      <FloatingIcon icon={IconFlame} size={50} color="#9B6BFF" top="88%" left="3%" rotate={-8} opacity={0.13} />

      {/* Decorative Icons — right side */}
      <FloatingIcon icon={IconHexagon} size={52} color="#4A1DC9" top="6%" right="5%" rotate={10} opacity={0.13} />
      <FloatingIcon icon={IconAtom} size={56} color="#5622F4" top="20%" right="3%" rotate={-10} opacity={0.15} />
      <FloatingIcon icon={IconDiamond} size={50} color="#7B4DFF" top="35%" right="4%" rotate={-20} opacity={0.14} />
      <FloatingIcon icon={IconFlame} size={58} color="#9B6BFF" top="50%" right="3%" rotate={25} opacity={0.13} />
      <FloatingIcon icon={IconStar} size={54} color="#5622F4" top="65%" right="5%" rotate={-5} opacity={0.14} />
      <FloatingIcon icon={IconSparkles} size={46} color="#4A1DC9" top="80%" right="4%" rotate={-15} opacity={0.16} />

      {/* Hero Section */}
      <Box className={classes.root}>
        <Box className={classes.grid}>
          <Box className={classes.section}>
            <Center px={50} pt={30} pb={10}>
              <Stack spacing={0} justify="center">
                <Center>
                  <Group spacing={10} align="center" sx={{ cursor: "pointer" }} onClick={() => navigation("/")}>
                    <Image
                      src="/mineralis-ico.svg"
                      alt="Mineralis"
                      width={48}
                      height={48}
                      sx={{ objectFit: "contain" }}
                    />
                    <Text
                      weight={800}
                      size={30}
                      align="center"
                      sx={{ color: "#6A1089" }}
                    >
                      Mineralis
                    </Text>
                  </Group>
                </Center>

                <Text
                  weight={900}
                  size={46}
                  align="center"
                  sx={{
                    color: "#000000",
                    "@media (max-width: 565px)": {
                      fontSize: "28px",
                      lineHeight: "2.1rem",
                    },
                  }}
                >
                  Rock Identifier & Mineral Scanner
                </Text>
                <Text
                  weight={400}
                  size={20}
                  align="center"
                  sx={{
                    color: "#87808A",
                    "@media (max-width: 565px)": {
                      fontSize: "1rem",
                    },
                  }}
                >
                  Identify any stone, mineral, or crystal with your camera!
                </Text>
              </Stack>
            </Center>
          </Box>

          {/* Hero Image + CTA */}
          <Box className={classes.section}>
            <Image
              src="/images/892_1x_shots_so.webp"
              alt="Mineralis app preview"
              radius={36}
            />
            <Center mt={20}>
              <Anchor href="#" target="_blank">
                <Image
                  src="/images/app-store-button-dark.svg"
                  width={200}
                  alt="Download on the App Store"
                />
              </Anchor>
            </Center>
          </Box>
          <Space h={30} />
        </Box>
      </Box>

      {/* Feature 1: Scan Any Stone */}
      <Box sx={{ width: "100%", backgroundColor: "#FFFFFF", position: "relative", zIndex: 1 }}>
        <Box className={classes.root}>
          <Box className={classes.grid} mt={0}>
            <Box className={classes.sectionTwo} py={30}>
              <Stack spacing={0} p={20}>
                <Text
                  weight={900}
                  size={36}
                  align="left"
                  sx={{
                    color: "#000000",
                    "@media (max-width: 565px)": {
                      fontSize: "1.5rem",
                      lineHeight: "2rem",
                    },
                  }}
                >
                  Scan Any Stone
                </Text>
                <Text
                  weight={400}
                  size={18}
                  align="left"
                  sx={{
                    color: "#87808A",
                    "@media (max-width: 565px)": {
                      fontSize: "1rem",
                    },
                  }}
                >
                  Point your camera at any rock, mineral, or gemstone for instant AI identification.
                </Text>
              </Stack>
              <Image src="/images/372_1x_shots_so.webp" alt="Scan any stone" />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Feature 2: Understand Your Find */}
      <Box sx={{ width: "100%", backgroundColor: "#5622F4", position: "relative", zIndex: 1 }}>
        <Box className={classes.root}>
          <Box className={classes.grid} mt={0}>
            <Box className={classes.sectionTwo} py={30}>
              <Image
                src="/images/951_1x_shots_so.webp"
                alt="Understand your find"
              />
              <Stack spacing={0} p={20}>
                <Text
                  weight={900}
                  size={36}
                  align="left"
                  sx={{
                    color: "#FFFFFF",
                    "@media (max-width: 565px)": {
                      fontSize: "1.5rem",
                      lineHeight: "1.8rem",
                    },
                  }}
                >
                  Understand Your Find
                </Text>
                <Text
                  weight={400}
                  size={18}
                  align="left"
                  sx={{
                    color: "rgba(255,255,255,0.7)",
                    "@media (max-width: 565px)": {
                      fontSize: "1rem",
                    },
                  }}
                >
                  Get detailed info on authenticity, properties, value tier, and practical uses.
                </Text>
              </Stack>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Feature 3: Build Your Collection */}
      <Box sx={{ width: "100%", backgroundColor: "#FFFFFF", position: "relative", zIndex: 1 }}>
        <Box className={classes.root}>
          <Box className={classes.grid} mt={0}>
            <Box className={classes.sectionTwo} py={30}>
              <Stack spacing={0} p={20}>
                <Text
                  weight={900}
                  size={36}
                  align="left"
                  sx={{
                    color: "#000000",
                    "@media (max-width: 565px)": {
                      fontSize: "1.5rem",
                      lineHeight: "1.8rem",
                    },
                  }}
                >
                  Build Your Collection
                </Text>
                <Text
                  weight={400}
                  size={18}
                  align="left"
                  sx={{
                    color: "#87808A",
                    "@media (max-width: 565px)": {
                      fontSize: "1rem",
                    },
                  }}
                >
                  Save your finds, add personal notes, create wishlists, and track your mineral journey.
                </Text>
              </Stack>
              <Image
                src="/images/682_1x_shots_so.webp"
                alt="Build your collection"
              />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Feature 4: Chat with AI Mineralogist */}
      <Box sx={{ width: "100%", backgroundColor: "#5622F4", position: "relative", zIndex: 1 }}>
        <Box className={classes.root}>
          <Box className={classes.grid} mt={0}>
            <Box className={classes.sectionTwo} py={30}>
              <Image
                src="/images/787_1x_shots_so.webp"
                alt="Chat with AI Mineralogist"
                sx={{ maxWidth: 500 }}
              />
              <Stack spacing={0} p={20}>
                <Text
                  weight={900}
                  size={36}
                  align="left"
                  sx={{
                    color: "#FFFFFF",
                    "@media (max-width: 565px)": {
                      fontSize: "1.5rem",
                      lineHeight: "1.8rem",
                    },
                  }}
                >
                  Chat with AI Mineralogist
                </Text>
                <Text
                  weight={400}
                  size={18}
                  align="left"
                  sx={{
                    color: "rgba(255,255,255,0.7)",
                    "@media (max-width: 565px)": {
                      fontSize: "1rem",
                    },
                  }}
                >
                  Ask anything about your stone — get expert answers on healing properties, value, care, and more.
                </Text>
              </Stack>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Light & Dark Mode + Final CTA */}
      <Box className={classes.root} sx={{ position: "relative", zIndex: 1 }}>
        <Box className={classes.grid}>
          <Box className={classes.section}>
            <Center p={50}>
              <Stack spacing={0} justify="center">
                <Text
                  weight={900}
                  size={36}
                  align="center"
                  sx={{
                    color: "#000000",
                    "@media (max-width: 565px)": {
                      fontSize: "1.7rem",
                      lineHeight: "2rem",
                    },
                  }}
                >
                  Light & Dark Mode
                </Text>
                <Text
                  weight={400}
                  size={18}
                  align="center"
                  sx={{
                    color: "#87808A",
                    "@media (max-width: 565px)": {
                      fontSize: "1rem",
                    },
                  }}
                >
                  Optimized for comfortable use day or night.
                </Text>
              </Stack>
            </Center>
          </Box>
          <Box className={classes.section}>
            <Image
              src="/images/709_1x_shots_so.webp"
              alt="Light and dark mode"
              radius={36}
            />
            <Center mt={20}>
              <Anchor href="#" target="_blank">
                <Image
                  src="/images/app-store-button-dark.svg"
                  width={200}
                  alt="Download on the App Store"
                />
              </Anchor>
            </Center>
          </Box>
          <Space h={30} />
        </Box>
      </Box>

      {/* Footer */}
      <Box className={classes.root} sx={{ position: "relative", zIndex: 1 }}>
        <Box className={classes.grid} mt={30}>
          <Box className={classes.sectionTwo}>
            <Text weight={400} size={16} align="left" sx={{ color: "#87808A" }}>
              Made with love by Anatoli Niculeac<sup>&copy;</sup> 2026
            </Text>
            <Group spacing={10} p={20} position="right">
              <Anchor component={Link} to={"/support"} className={classes.link}>
                <Text weight={500} size={16} align="left" sx={{ color: "#5622F4" }}>
                  Support
                </Text>
              </Anchor>
              <Text weight={400} size={16} align="left" sx={{ color: "#87808A" }}>
                |
              </Text>
              <Anchor component={Link} to={"/terms"} className={classes.link}>
                <Text weight={500} size={16} align="left" sx={{ color: "#5622F4" }}>
                  Terms of use
                </Text>
              </Anchor>
              <Text weight={400} size={16} align="left" sx={{ color: "#87808A" }}>
                |
              </Text>
              <Anchor component={Link} to={"/privacy"} className={classes.link}>
                <Text weight={500} size={16} align="left" sx={{ color: "#5622F4" }}>
                  Privacy Policy
                </Text>
              </Anchor>
              <Text weight={400} size={16} align="left" sx={{ color: "#87808A" }}>
                |
              </Text>
              <Anchor component={Link} to={"/contact"} className={classes.link}>
                <Text weight={500} size={16} align="left" sx={{ color: "#5622F4" }}>
                  Contact
                </Text>
              </Anchor>
            </Group>
            <Space h={10} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default HomePage;

const useStyles = createStyles((theme) => ({
  pageWrapper: {
    width: "100%",
    minHeight: "100vh",
    background:
      "radial-gradient(ellipse at 15% 20%, rgba(86, 34, 244, 0.08) 0%, transparent 50%), " +
      "radial-gradient(ellipse at 85% 40%, rgba(123, 77, 255, 0.08) 0%, transparent 50%), " +
      "radial-gradient(ellipse at 20% 70%, rgba(74, 29, 201, 0.06) 0%, transparent 45%), " +
      "radial-gradient(ellipse at 80% 85%, rgba(86, 34, 244, 0.06) 0%, transparent 45%), " +
      "linear-gradient(180deg, #EDE8FE 0%, #F0ECFF 100%)",
    position: "relative",
    overflow: "hidden",
  },

  root: {
    height: "100%",
    width: "100%",
    overflowX: "hidden",
    display: "flex",
    justifyContent: "space-between",
    flexDirection: "column",
  },

  section: {
    maxWidth: 980,
    width: "90%",
    marginLeft: "auto",
    marginRight: "auto",
    display: "grid",
    gridTemplateColumns: "1fr",
    gridTemplateRows: "1fr",
    gridGap: "1em",
    gap: "1em",
    alignItems: "center",
    paddingLeft: 15,
    paddingRight: 15,
  },

  sectionTwo: {
    maxWidth: 980,
    width: "90%",
    marginLeft: "auto",
    marginRight: "auto",
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gridTemplateRows: "1fr",
    gridGap: "1em",
    gap: "1em",
    alignItems: "center",
    paddingLeft: 15,
    paddingRight: 15,
  },

  grid: {
    display: "grid",
    width: "100%",
    height: "100%",
    gridTemplateColumns: "auto",
    gridTemplateRows: "auto",
    "@media (max-width: 1023px)": {
      gridTemplateColumns: "auto",
      justifyContent: "stretch",
    },
    "@media (min-width: 1600px)": {
      gridTemplateColumns: "auto",
      gridTemplateRows: "auto",
      WebkitTransition: "300ms",
    },
  },

  link: {
    whiteSpace: "nowrap",
    textDecoration: "none",
    ":last-child": {
      borderRight: "none",
    },
    webkitTransition: "opacity 0.3s cubic-bezier(0.25, 0.1, 0.25, 1)",
    transition: "opacity 0.3s cubic-bezier(0.25, 0.1, 0.25, 1)",
    ":hover": {
      transition: "0.5s color",
      textDecoration: "none",
      opacity: 0.7,
    },
  },
}));
